import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import Card from 'components/Layout/Card'
import PageHeader from 'components/Layout/PageHeader'
import FillingSpinner, { FillingSpinnerType } from 'components/FillingSpinner'
import Button, { ButtonTheme } from 'components/Form/Button'
import { NotifierType } from 'components/Notifier'
import api from 'api'
import { DATE_HOUR_FORMAT } from 'constant'
import { AppContext } from 'contexts/AppContext'
import DateUtils from 'utils/date'
import { AiRecommendationPromptModel } from 'api/broadSign/models/ai-recommendation-prompt.model'
import './AiRecommendationPrompt.scss'

const AiRecommendationPrompt: React.FC = () => {
  const { t } = useTranslation()
  const { addNotification } = useContext(AppContext)
  const [prompt, setPrompt] = useState<AiRecommendationPromptModel | null>(null)
  const [content, setContent] = useState('')
  const [saving, setSaving] = useState(false)

  const loadPrompt = (loaded: AiRecommendationPromptModel) => {
    setPrompt(loaded)
    setContent(loaded.content)
  }

  useEffect(() => {
    void api.broadSign
      .getAiRecommendationPrompt()
      .then(({ data }) => data && loadPrompt(data.aiRecommendationPrompt))
      .catch(() => void {})
  }, [])

  const savePrompt = () => {
    setSaving(true)

    void api.broadSign
      .updateAiRecommendationPrompt(content)
      .then(({ data }) => {
        const result = data?.updateAiRecommendationPrompt
        if (!result || result.errors.length) {
          addNotification(NotifierType.ERROR, t('broadSign.aiPrompt.updateFailed'))
          return
        }

        loadPrompt(result.aiRecommendationPrompt)
        addNotification(NotifierType.SUCCESS, t('broadSign.aiPrompt.updateSuccessful'))
      })
      .catch(() => addNotification(NotifierType.ERROR, t('broadSign.aiPrompt.updateFailed')))
      .finally(() => setSaving(false))
  }

  return (
    <div className='AiRecommendationPrompt'>
      <PageHeader>{t('broadSign.aiPrompt.title')}</PageHeader>

      <Card>
        {prompt ? (
          <>
            <p className='AiRecommendationPrompt__description'>
              {t('broadSign.aiPrompt.description')}
            </p>
            <textarea
              className='Input__field AiRecommendationPrompt__input'
              value={content}
              onChange={event => setContent(event.target.value)}
              data-cy='ai-recommendation-prompt-input'
            />
            <div className='AiRecommendationPrompt__footer'>
              <span className='AiRecommendationPrompt__updated-at'>
                {t('broadSign.aiPrompt.updatedAt')}:{' '}
                {DateUtils.parseAndFormat(prompt.updatedAt, DATE_HOUR_FORMAT)}
              </span>
              <Button
                theme={ButtonTheme.BLUE}
                icon={saving ? faSpinner : undefined}
                // the backend refuses a blank prompt, whitespace counts as blank there too
                disabled={saving || !content.trim() || content === prompt.content}
                onClick={savePrompt}
                dataCy='ai-recommendation-prompt-save-button'
              >
                {t('common.save')}
              </Button>
            </div>
          </>
        ) : (
          <FillingSpinner type={FillingSpinnerType.STANDARD} />
        )}
      </Card>
    </div>
  )
}

export default AiRecommendationPrompt
